// qeeChatEmbedded.js

/**
 * Embedded QEE chat surface.
 * Developers: Adrian Engelmann, Kiran Jayaramu, Nicu Bratescu, Daniela Popescu
 * Copyright 2026 by Qalgo GmbH (www.qalgo.de). All rights reserved.
 */
import react, { useState, useEffect, useLayoutEffect, useRef } from 'react'; 
import React, { useImperativeHandle } from 'react';

import withStyles from '@material-ui/core/styles/withStyles'; 
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import { useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'; 

import {QeeChatController} from './qeeChatController.js';
import {QeeMuiChat} from './qeeMuiChat.js';
import QeeSlideDialog from './qeeSlideDialog';
import {QeeColorButtonPrimary, QeeColorButtonSecondary} from './qeeColorButton';
import {findGuruExportSource} from './guruExportSource';
import {mergeGuruPageQuestion, selectGuruNudges} from './guruNudge';
import GuruChatWorkspace from './guruChatHistory/GuruChatWorkspace';
import {resolveGuruUiLanguage} from './guruChatHistory/guruUiLanguage';

import { markChatRead }  from '../api/service';

import WaitProgressLinear from "./waitProgressLinear";

import { useSnackbar } from 'notistack';

import { 
	getCfgsNet, 
	getOpenaiDesc, 
	postOpenaiAnonymousMessage, 
	postSynapcusGuruSupportRequest,
	postChatMessage, 	
	postChatFieldValue, 	
	getAnonymousChat,
	getChatBySessId,
	getFacChat,
	getChatConfig	
} from "../api/service";
import { 
	generateRandomNumber, 
	getUserIP, 
	convertObjToDateTime	
} from '../util/helpers';

//import css from '../core/App.css?inline';

const styles = (theme) => ({
});

const getConfiguredChatFallback = (chatConfig, language) => {
	const config = chatConfig && typeof chatConfig === 'object' ? chatConfig : {};
	const dynamicConfig = config.chat_dynamic_conversation && typeof config.chat_dynamic_conversation === 'object'
		? config.chat_dynamic_conversation
		: {};
	const localized = dynamicConfig.fallbackResponses && typeof dynamicConfig.fallbackResponses === 'object'
		? dynamicConfig.fallbackResponses
		: {};
	const lang = String(language || 'de').toLowerCase().split(/[-_]/)[0];
	return String(
		localized[lang]
		|| localized.en
		|| dynamicConfig.fallbackResponse
		|| config.chat_initial_msg
		|| ''
	).trim();
};

const readStandaloneGuruLaunchContext = () => {
	if (window.parent !== window) return {showHistory: false, helperQuestions: []};
	try {
		const context = JSON.parse(window.sessionStorage.getItem('synapcus.guru.launchContext') || '{}');
		const helperQuestions = Array.isArray(context.helperQuestions)
			? context.helperQuestions.slice(0, 4).map(item => ({
				question: String(item && item.question || '').trim().slice(0, 500),
				prompt: String(item && item.prompt || item && item.question || '').trim().slice(0, 2000)
			})).filter(item => item.question && item.prompt)
			: [];
		return {showHistory: context.showHistory === true, helperQuestions};
	} catch (error) {
		return {showHistory: false, helperQuestions: []};
	}
};


// main fct
const QeeChatEmbedded = React.forwardRef((props, forwardedRef) => {

	const theme = useTheme();
	
	const {height, parentForm, parentId, isP2P, showDateTime, showDate, showTime } = props;

	const [chatCtl, setChatCtl] = React.useState(new QeeChatController({
		showDateTime,
		showDate,
		showTime,
		parentForm,  // Add this line
		parentId     // Add this line
	}));	
	const standaloneLaunchContext = React.useMemo(readStandaloneGuruLaunchContext, []);

	const { enqueueSnackbar } = useSnackbar();	

	const isInitializingChatRef = useRef(false);
	const [isError, setIsError] = useState( null);			
	const [chatCfg, setChatCfg] = useState( null);			
	const savedChat = sessionStorage.getItem('chat');
	let initialChat = {};
	if (savedChat && savedChat !== 'null' && savedChat !== 'undefined') {
		try {
			const parsedChat = JSON.parse(savedChat);
			if (parsedChat && typeof parsedChat === 'object') initialChat = parsedChat;
		} catch (error) {
			sessionStorage.removeItem('chat');
		}
	}
	const [chat, setChat] = useState(initialChat);
	const [hasErrorsDlgChat, setHasErrorsDlgChat] = useState( true);				
	const [changesDlgChat, setChangesDlgChat] = useState( {});	
	const isProcessingStepsRef = useRef(false);
	const pendingPageQuestionRef = useRef(false);
	const currentPageRef = useRef('');
	const activeNudgeRef = useRef(null);
	const activeNudgesRef = useRef([]);
	const pendingNudgePromptRef = useRef('');
				
	const [state, setState] = useState({
		url: '',
		submitDisabled: true,
	});
	
	const [isReady, setIsReady] = useState(false); 	
	const [historyRevision, setHistoryRevision] = useState(0);
	
	const chatRef = useRef(chat);
	
	const isMounted = useRef(false);	


	// kill the body overflow
	useEffect(() => {
		const originalOverflow = document.body.style.overflow;
		const originalOverflowPriority = document.body.style.getPropertyPriority('overflow');
		const originalRootOverflow = document.documentElement.style.overflow;
		const originalRootOverflowPriority = document.documentElement.style.getPropertyPriority('overflow');

		// The Domino embed stylesheet uses !important while loading. Keep the
		// document itself fixed once React owns scrolling inside the chat.
		document.body.style.setProperty('overflow', 'hidden', 'important');
		document.documentElement.style.setProperty('overflow', 'hidden', 'important');

		return () => {
			document.body.style.setProperty('overflow', originalOverflow, originalOverflowPriority);
			document.documentElement.style.setProperty('overflow', originalRootOverflow, originalRootOverflowPriority);
		};
	}, []);

	
	useEffect(() => {				
		if (chat && chat.id) {
			chatRef.current = chat;
		}
	}, [chat?.id]);

	
	useEffect(() => {
        isMounted.current = true;

        // Async function to handle chat initialization
        const initializeChat = async () => {
			if (isInitializingChatRef.current) return;
			isInitializingChatRef.current = true;
			setIsError(null);

            // Initialize the chat controller if it hasn't been initialized yet
            if (!chatCtl) {
				const newChatCtl = new QeeChatController({
													showDateTime,
													showDate,
													showTime,
													parentForm,  // Add this line
													parentId     // Add this line
				});				
                if (isMounted.current) {
                    setChatCtl(newChatCtl);
                }
            }
			
			// Attempt to fetch chat configuration
			try {
				const chatConfig = await getChatConfig( props.instanceName);
				if (isMounted.current && chatConfig) {
					setChatCfg( chatConfig);
					
					// Fetch chat data here 
					await handleChatOpen( chatConfig);
				}
			} catch (error) {
				if (isMounted.current) {
					console.error(error);
					setIsError(error.message);
				}
			} finally {
				isInitializingChatRef.current = false;
				if (pendingPageQuestionRef.current) showCurrentPageQuestion();
			}



            // Set ready state if everything is initialized correctly
            if (isMounted.current) {
                setIsReady(true);
            }
        };

        // Call the async function
        initializeChat();

        // Cleanup function to set isMounted to false when the component unmounts
        return () => {
            isMounted.current = false;
        };
    }, []); 	
	
	
	// some consts
	const useStyles = makeStyles( theme => ({
		//...props
		accordionSummary: {
			backgroundColor: '#f00'
		},
		accordionBox: {
			fontWeight:'bold', 
			m:1, 
			lineHeight:1.2, 
			color:theme.palette.primary.contrastText,
		},
		icon:{
			color:theme.palette.primary.contrastText,
		}
	}));			
	const classes = useStyles( props); 


	/**
	*	expose it outside
	*/
	useImperativeHandle( forwardedRef, () => ({	
		ali: ()=>{alert(123)},
		handleChatOpen: handleChatOpen,						
		handleChatClose: handleChatClose,
		submitNudge: submitGuruNudge
	}));	
	
		
	const convertQAtoOptions = ( qaArray) => {
		
		console.log( '--> convertQAtoOptions', qaArray);
		
		var arrRet =  qaArray.map((item, index) => ({
			value: index.toString(), // Convert index to string to use as value
			text: item.question, // Use the question as the option text
			prompt: item.prompt || item.question, // Send the precise prompt while displaying the short label
			answerType: item.answerType, // Retain the original answerType			
			answer: item.answer, // Retain the original answer
			quickQuestions: item.quickQuestions, // Retain the original quickQuestions
			followUpContext: item.followUpContext
		}));
		
		console.log( '<-- convertQAtoOptions', arrRet);
		
		return arrRet;
	};	

	const getCurrentPageQuestion = () => {
		if (activeNudgeRef.current) {
			return {
				question: activeNudgeRef.current.question,
				prompt: activeNudgeRef.current.prompt,
				answerType: 'predefined-intent',
				answer: 'SynapcusAktuelleMaske',
				guruNudge: true
			};
		}
		const config = chatCtl && chatCtl.chatConfig;
		const questions = config && Array.isArray(config.chat_initial_qa)
			? config.chat_initial_qa : [];
		return questions.find(item => item && item.answer === 'SynapcusAktuelleMaske') || null;
	};

	const getNudgeAwareInitialQuestions = questions => {
		const contextual = activeNudgesRef.current.map(nudge => ({
			question: nudge.question,
			text: nudge.question,
			prompt: nudge.prompt,
			answerType: 'predefined-intent',
			answer: 'SynapcusAktuelleMaske',
			guruNudge: true
		}));
		if (!contextual.length) return mergeGuruPageQuestion(questions, getCurrentPageQuestion());
		const freeTextOptions = (Array.isArray(questions) ? questions : [])
			.filter(item => item && item.answerType === 'predefined-steps');
		return contextual.concat(freeTextOptions).slice(0, 6);
	};

	const publishGuruNudge = nudge => {
		if (!nudge || !nudge.question || !nudge.prompt) return;
		try {
			if (typeof props.onNudgeReady === 'function') props.onNudgeReady(nudge);
			if (window.parent && window.parent !== window) {
				window.parent.postMessage({type: 'synapcus:guru-nudge', nudge}, window.location.origin);
			}
		} catch (error) {
			// Proactive suggestions are optional and must never affect the chat.
		}
	};

	const updateActiveNudge = (config, context) => {
		const nudges = selectGuruNudges(config || (chatCtl && chatCtl.chatConfig), context || {}, 5);
		activeNudgesRef.current = nudges;
		const nudge = nudges[0] || null;
		activeNudgeRef.current = nudge;
		if (nudge) publishGuruNudge(nudge);
		return nudge;
	};

	const submitGuruNudge = (prompt, question) => {
		const safePrompt = String(prompt || '').trim().slice(0, 2000);
		if (!safePrompt) return false;
		const safeQuestion = String(question || safePrompt).trim().slice(0, 500);
		if (!isReady || isInitializingChatRef.current || !chatCtl || !chatRef.current || !chatRef.current.id) {
			pendingNudgePromptRef.current = {prompt: safePrompt, question: safeQuestion};
			return true;
		}
		pendingNudgePromptRef.current = '';
		chatCtl.setActionRequest({type: 'text', always: true, self: true});
		chatCtl.addMessage({type: 'text', content: safeQuestion, prompt: safePrompt,
			self: true, guruNudge: true});
		return true;
	};

	useEffect(() => {
		if (!isReady || !pendingNudgePromptRef.current) return;
		const pending = pendingNudgePromptRef.current;
		pendingNudgePromptRef.current = '';
		// Keep older callers safe: before prompt/question were separated, the
		// pending value was stored as a plain string.
		if (typeof pending === 'string') submitGuruNudge(pending, pending);
		else submitGuruNudge(pending.prompt, pending.question);
	}, [isReady]);

	useEffect(() => {
		const handleParentNudge = event => {
			if (event.origin !== window.location.origin || event.source !== window.parent || !event.data) return;
			if (event.data.type === 'synapcus:request-guru-nudge') {
				if (activeNudgeRef.current) publishGuruNudge(activeNudgeRef.current);
			} else if (event.data.type === 'synapcus:run-guru-nudge') {
				submitGuruNudge(event.data.prompt, event.data.question);
			}
		};
		window.addEventListener('message', handleParentNudge);
		return () => window.removeEventListener('message', handleParentNudge);
	}, [chatCtl, isReady]);

	const mergeCurrentPageQuestion = (questions) => {
		const pageQuestion = getCurrentPageQuestion();
		return mergeGuruPageQuestion(questions, pageQuestion);
	};

	const showCurrentPageQuestion = () => {
		const pageQuestion = getCurrentPageQuestion();
		if (!pageQuestion || isInitializingChatRef.current || isProcessingStepsRef.current) return false;
		const messages = chatCtl.getMessages();
		if (messages.length && messages[messages.length - 1].self) return false;
		const actionRequest = chatCtl.getActionRequest() || {};
		if (actionRequest.isInitialQuestions && Array.isArray(actionRequest.options) &&
			actionRequest.options.some(item => item && item.answer === 'SynapcusAktuelleMaske')) {
			chatCtl.setActionRequest({
				...actionRequest,
				options: mergeGuruPageQuestion(actionRequest.options, pageQuestion)
			});
			pendingPageQuestionRef.current = false;
			return true;
		}
		chatCtl.setActionRequest({
			type: 'text',
			always: true,
			self: true,
			quickQuestions: mergeCurrentPageQuestion(actionRequest.quickQuestions)
		});
		pendingPageQuestionRef.current = false;
		return true;
	};

	useEffect(() => {
		const handleSynapcusContextChanged = event => {
			const nextPage = event && event.detail && typeof event.detail.currentPage === 'string'
				? event.detail.currentPage : '';
			if (!nextPage || nextPage === currentPageRef.current) return;
			currentPageRef.current = nextPage;
			updateActiveNudge(chatCtl && chatCtl.chatConfig, event.detail || {currentPage: nextPage});
			pendingPageQuestionRef.current = true;
			showCurrentPageQuestion();
		};
		window.addEventListener('synapcus:context-changed', handleSynapcusContextChanged);
		return () => window.removeEventListener('synapcus:context-changed', handleSynapcusContextChanged);
	}, [chatCtl]);
	
	
	/**
	*	handleChatOpen() - starts the chat
	*/
	const handleChatOpen = async (chatConfig, historyRequest = {}) => {
	
		console.log( 'handleChatOpen', chatConfig);
		
		let oChatConfig = JSON.parse( chatConfig.json);
		console.log( 'Parsed oChatConfig', oChatConfig);
	
		// try to get the local usr
		let anonymSess;
		let usrId;
		var usr = sessionStorage.getItem( 'usr');
		if( usr) {
			usr = JSON.parse( usr);
			usrId = usr.usrId;
			anonymSess = usr.qeeAnonymous === true ||
				/^(?:\d{1,3}\.){3}\d{1,3}$/.test(String(usrId || ''));
		}
		// if not found, for anonymous sessions get its IP
		if (!usrId) {
			var usr = {usrId:await getUserIP(), qeeAnonymous:true};
			sessionStorage.setItem( 'usr', JSON.stringify( usr))
			anonymSess = true;
		}
		const guruHistoryEnabled = oChatConfig.chat_cfg_name === 'synapcus-chat-guru';
		
		var chat = null;
		var sChat = sessionStorage.getItem( 'chat');
		if (guruHistoryEnabled && historyRequest.newChat) {
			sChat = null;
			sessionStorage.removeItem('chat');
		} else if (guruHistoryEnabled && historyRequest.chatId) {
			sChat = JSON.stringify({id: historyRequest.chatId, chat_fac_id: historyRequest.chat_fac_id});
		}
		if (sChat && sChat !== 'null' && sChat !== 'undefined') {
			try {
				chat = JSON.parse(sChat);
			} catch (error) {
				chat = null;
			}
			if (!chat || typeof chat !== 'object' || !chat.chat_fac_id) {
				chat = null;
				sessionStorage.removeItem('chat');
			}
		} else if (sChat) {
			sessionStorage.removeItem('chat');
		}

		// Resume the existing conversation. Only create a new session id when
		// there is no valid stored chat and no parent object defines one.
		var sessId = chat && chat.chat_fac_id
			? chat.chat_fac_id
			: 'SESS-'+generateRandomNumber( 7);
		const explicitHistoryChat = guruHistoryEnabled && Boolean(historyRequest.chatId);
		if (parentForm && parentId && !explicitHistoryChat) {
			sessId = parentForm+'-'+parentId;
		}
		if (chat && chat.chat_fac_id !== sessId && !explicitHistoryChat) {
			chat = null;
			sessionStorage.removeItem('chat');
		}

		const storedChat = chat;
		let resumedStoredChat = false;
		if (storedChat && storedChat.id && !oChatConfig.isP2P) {
			const resumedChat = await getAnonymousChat(sessId, storedChat.id, chatConfig);
			if (resumedChat && !resumedChat.err && resumedChat.id) {
				const primaryBrowserLanguage = Array.isArray(window.navigator.languages)
					? window.navigator.languages.find(language => typeof language === 'string' && language.trim())
					: '';
				const desiredLocale = String(oChatConfig.chat_config_localized_for || primaryBrowserLanguage || window.navigator.language || 'en')
					.toLowerCase().split(/[-_]/)[0];
				const resumedLocale = String(resumedChat.chat_language || '').toLowerCase().split(/[-_]/)[0];
				const languageChanged = ['synapcus-chat-qalgo1', 'synapcus-chat-guru'].includes(oChatConfig.chat_cfg_name) &&
					!explicitHistoryChat &&
					resumedLocale && desiredLocale && resumedLocale !== desiredLocale;
				if (languageChanged) {
					// A website chat belongs to the language in which it was started.
					// Start a fresh chat after the browser language changes so prompts,
					// answers and UI labels cannot be mixed across languages.
					chat = null;
					sessId = 'SESS-'+generateRandomNumber(7);
					sessionStorage.removeItem('chat');
				} else {
					chat = resumedChat;
					resumedStoredChat = true;
				}
			} else if (resumedChat && resumedChat.err) {
				throw new Error('Der bestehende Chat konnte nicht geladen werden: ' + resumedChat.err);
			} else {
				chat = null;
				sessionStorage.removeItem('chat');
			}
		}

		// --- for anonymous
		if( !resumedStoredChat && anonymSess) {
			// first try here
			console.log( 'openChatDialog chat from sessionStorage', chat);
			chat = await getAnonymousChat( sessId, chat ? chat.id : null, chatConfig);
			if( chat && !chat.err && chat.id) {
				sessionStorage.setItem( 'chat', JSON.stringify({
						id:chat.id, 
						chat_fac_id:chat.chat_fac_id
					})
				);
			// second try, if the chatid in the sessionStorage is set, but it was not found on db / does not exist	
			} else if (!chat || !chat.err) {
				chat = await getAnonymousChat( sessId, null, chatConfig);
				if( chat && !chat.err && chat.id) {
					sessionStorage.setItem( 'chat', JSON.stringify({
							id:chat.id, 
							chat_fac_id:chat.chat_fac_id
						})
					);			
				}	
			}
			
		// --- for authorized chats
		} else if( !resumedStoredChat) {
			console.log( 'openChatDialog chat from sessionStorage', chat);
			chat = await getChatBySessId( sessId, chatConfig);
			if( chat && !chat.err) {
				sessionStorage.setItem( 'chat', JSON.stringify({
						id:chat.id, 
						chat_fac_id:chat.chat_fac_id
					})
				);
			} else {
				chat = await getAnonymousChat( sessId, null, chatConfig);
				if( chat && !chat.err && chat.id) {
					sessionStorage.setItem( 'chat', JSON.stringify({
							id:chat.id, 
							chat_fac_id:chat.chat_fac_id
						})
					);			
				}	
			}
		}
		if (resumedStoredChat) {
			sessionStorage.setItem('chat', JSON.stringify({
				id: chat.id,
				chat_fac_id: chat.chat_fac_id
			}));
		}
		if (!chat || chat.err || !chat.id) {
			const loadError = chat && chat.err ? chat.err : 'Keine gültigen Chatdaten erhalten.';
			throw new Error(typeof loadError === 'string' ? loadError : JSON.stringify(loadError));
		}
		var newChat = chat;		
		console.log('handleChatOpen chat', newChat);
				
		chatRef.current = newChat;
		isProcessingStepsRef.current = false;
		
		// evtl. remove the chat which is empty
		if( !newChat) {
			sessionStorage.removeItem( 'chat');
		}
		
		// Clear all existing messages in the ChatController
		if( chatCtl) {
			chatCtl.clearMessages();
		}
		
		chatCtl.chatConfig = JSON.parse( chatConfig.json);
		console.log( 'Parsed chatConfig', chatCtl.chatConfig);		
		try {
			const nudgeContext = window.SynapcusGuru && typeof window.SynapcusGuru.getContext === 'function'
				? window.SynapcusGuru.getContext()
				: {currentPage: window.location.href, clientSurface: 'public-web', referrer: document.referrer};
			updateActiveNudge(chatCtl.chatConfig, nudgeContext);
		} catch (error) {
			activeNudgeRef.current = null;
			activeNudgesRef.current = [];
		}
		chatCtl.removeOnMessagesChanged(onMessagesChanged);
		chatCtl.addOnMessagesChanged(onMessagesChanged);		

		// Add messages from newChat.msgs array to the ChatController
		if( newChat) {
			var msgs = [];
			for (const msg of (newChat.chat_msgs || [])) {
				msgs.push({
					type: msg.type,
					content: msg.content,
					uploadId: msg.type==='file' ? msg.uploadId : null,
					username: msg.username,					
					self: msg.self,
					avatar: msg.self ? '' : <TipsAndUpdatesIcon />,
					usrId: msg.usrId,
					helpUsed: Boolean(msg.helpUsed),
					sources: Array.isArray(msg.sources) ? msg.sources : [],
					images: Array.isArray(msg.images) ? msg.images : [],
					assistantPresentation: msg.assistantPresentation || 'none',
					answerSource: msg.answerSource || (msg.helpUsed ? 'online-help' : 'general'),
					assistantDomain: msg.assistantDomain || 'general',
					guruPdfDownload: msg.guruPdfDownload && typeof msg.guruPdfDownload === 'object'
						? msg.guruPdfDownload : null,
					guruExcelDownload: msg.guruExcelDownload && typeof msg.guruExcelDownload === 'object'
						? msg.guruExcelDownload : null,
					guruExportEligible: msg.guruExportEligible !== false,
					guruExportPrepared: Boolean(msg.guruExportPrepared),
					guruExportCompleted: Boolean(msg.guruExportCompleted),
					offerHumanSupport: Boolean(msg.offerHumanSupport),
					supportNeedsEmail: Boolean(msg.supportNeedsEmail),
					createdAt: convertObjToDateTime( msg.createdAt),
					perName: msg.self ? '' : msg.per_name
					//username: msg.self ? 'eu' : '',
				});
			}
			if (standaloneLaunchContext.helperQuestions.length) {
				for (let index = msgs.length - 1; index >= 0; index -= 1) {
					if (!msgs[index].self) {
						msgs[index].quickQuestions = standaloneLaunchContext.helperQuestions;
						break;
					}
				}
			}
			console.log( 'setMessages', msgs);			
			await chatCtl.setMessages( msgs);			

			// A deliberately started new Guru chat must receive the same contextual
			// initial questions as the very first chat. Previously newChat forced the
			// plain text branch, where embedded Synapcus dialogs normally have no
			// standalone helper questions, so the current page/object question vanished.
			var oJsonChat = chatCtl.chatConfig;
			const showInitialQuestions = historyRequest.newChat || msgs.length <= 1;
			if (showInitialQuestions && Array.isArray(oJsonChat.chat_initial_qa) && oJsonChat.chat_initial_qa.length) {
				console.log('oJsonChat.chat_initial_qa', oJsonChat.chat_initial_qa);
				await chatCtl.setActionRequest({
					type: 'select',
					always: true,
					self: true,
					isInitialQuestions: true,
					options: convertQAtoOptions(getNudgeAwareInitialQuestions(oJsonChat.chat_initial_qa))
				});
			} else {
				await chatCtl.setActionRequest({
					type: 'text',
					always: true,
					self: true,
					quickQuestions: standaloneLaunchContext.helperQuestions
				});
			}
		}

		setChat((prevChat) => ({
			...prevChat,
			...newChat,
		}));
		if (guruHistoryEnabled) {
			setHistoryRevision(value => value + 1);
		}

		if( isP2P && newChat) {
			const result = await markChatRead(usrId, newChat.id);
			if (result.err) {
				console.error('ERR marking chat as read:', result.err);
			} else {
				console.log('Chat marked as read successfully:', result);
			}
		}		
		if (!isP2P) {
			try {
				const context = window.SynapcusGuru && typeof window.SynapcusGuru.getContext === 'function'
					? window.SynapcusGuru.getContext() : window.QEE_SYNAPCUS_CONTEXT;
				const currentPage = context && typeof context.currentPage === 'string'
					? context.currentPage : '';
				if (currentPage && currentPage !== currentPageRef.current) {
					currentPageRef.current = currentPage;
					pendingPageQuestionRef.current = true;
				}
			} catch (contextError) {
				// The next context event will retry without blocking chat startup.
			}
		}
		if (pendingPageQuestionRef.current) {
			showCurrentPageQuestion();
		}
		
	};

		
	const handleChatClose = () => {
	};

	const runGuruChatTransition = async historyRequest => {
		if (isInitializingChatRef.current || !chatCfg) return;
		let resetConfigName = chatCtl && chatCtl.chatConfig && chatCtl.chatConfig.chat_cfg_name;
		if (!resetConfigName && chatCfg.json) {
			try {
				resetConfigName = JSON.parse(chatCfg.json).chat_cfg_name;
			} catch (error) {
				resetConfigName = '';
			}
		}
		if (historyRequest.newChat && isP2P && resetConfigName !== 'synapcus-chat-guru') return;

		isInitializingChatRef.current = true;
		setIsReady(false);
		setIsError(null);
		try {
			if (historyRequest.newChat) sessionStorage.removeItem('chat');
			chatRef.current = {};
			setChat({});
			isProcessingStepsRef.current = false;
			chatCtl.clearMessages();
			await handleChatOpen(chatCfg, historyRequest);
		} catch (error) {
			console.error('Guru chat transition failed:', error);
			setIsError(error.message || String(error));
			throw error;
		} finally {
			isInitializingChatRef.current = false;
			setIsReady(true);
			if (pendingPageQuestionRef.current) showCurrentPageQuestion();
		}
	};

	const handleResetChat = () => runGuruChatTransition({newChat: true});
	const handleSelectHistoryChat = selected => runGuruChatTransition({
		chatId: selected.id,
		chat_fac_id: selected.chat_fac_id
	});
	
	
	/**
	*	_getIntentAnswer() - get answer for intent
	*/
	const _getIntentAnswer = (intent, oJsonChatCfg) => {

		console.log('--> _getIntentAnswer', intent);

		const { dynamicHandlers } = oJsonChatCfg.chat_dynamic_conversation;

		// Find the first handler that matches the provided message intent
		const handler = dynamicHandlers.find(handler => handler.intent === intent);

		let oAnsw = handler;
		
		console.log('<-- _getIntentAnswer', oAnsw);	
		return oAnsw;
	}	
	
	
	/**
	*	onMessagesChanged - callback when msgs changes
	*/
	const onMessagesChanged = async (msgs) => {
		console.log('--> onMessagesChanged', msgs);

		// Skip processing messages during initialization
		if (isInitializingChatRef.current) {
			console.log('<-- onMessagesChanged do nothing in init!');
			return;
		}

		const currentChat = chatRef.current;

		if (msgs.length === 0) {
			console.log('onMessagesChanged msgs.length==0');
			console.log('<-- onMessagesChanged no msgs!');
			return;
		}

		const lastMessage = msgs[msgs.length - 1];

		// Ensure the message isn't already an OpenAI response
		if (lastMessage.self && (lastMessage.type === 'text' || lastMessage.type === 'file')) {
			if (!isP2P) {
				// try to guess the answerType for intents
				var answ = _getIntentAnswer(lastMessage.content, chatCtl.chatConfig);
				if (answ) {
					lastMessage.answer = answ.answer;
					lastMessage.answerType = answ.answerType;
					lastMessage.confluencePage = answ.confluencePage;
				}

				if (lastMessage.answerType === 'predefined-steps') {
					processSteps(msgs, lastMessage.answer.steps);
				} else if (isProcessingStepsRef.current === false) {
					let openAIResponse;
					try {
						// goto server, he will decide about it
						let synapcusContext = null;
						try {
							synapcusContext = window.SynapcusGuru && typeof window.SynapcusGuru.getContext === 'function'
								? window.SynapcusGuru.getContext()
								: window.QEE_SYNAPCUS_CONTEXT;
						} catch (contextError) {
							synapcusContext = null;
						}
						// Always offer Synforge the exact immediately preceding Guru answer as
						// trusted export data. It is kept out of the model prompt unless the
						// model selects an export tool, so natural requests such as "als Excel"
						// work without asking the model to reproduce large tables as JSON.
						let requestMessage = lastMessage.prompt
							? {...lastMessage, content: lastMessage.prompt}
							: lastMessage;
						const activeChatConfigName = (chatCtl.chatConfig && chatCtl.chatConfig.chat_cfg_name) ||
							(currentChat && currentChat.chat_cfg_name) || props.instanceName || '';
						if (activeChatConfigName === 'synapcus-chat-guru' && !lastMessage.guruExportSource &&
							!lastMessage.excelExportSource && !lastMessage.pdfExportSource) {
							const exportSource = findGuruExportSource(msgs, msgs.length - 1);
							if (exportSource) requestMessage = {...requestMessage, guruExportSource: exportSource};
						}
						const respMsg = await postOpenaiAnonymousMessage(currentChat.id, requestMessage, synapcusContext);
						console.log('onMessagesChanged respMsg', respMsg);
						if (!respMsg || typeof respMsg.content !== 'string' || !respMsg.content.trim()) {
							throw new Error('The chat API returned no answer content.');
						}
						openAIResponse = {
							content: respMsg.content,
							quickQuestions: pendingPageQuestionRef.current
								? mergeCurrentPageQuestion(respMsg.quickQuestions)
								: respMsg.quickQuestions,
							answerType: respMsg.answerType,
							helpUsed: Boolean(respMsg.helpUsed),
							sources: Array.isArray(respMsg.sources) ? respMsg.sources : [],
							images: Array.isArray(respMsg.images) ? respMsg.images : [],
							assistantPresentation: respMsg.assistantPresentation || 'none',
							answerSource: respMsg.answerSource || (respMsg.helpUsed ? 'online-help' : 'general'),
							assistantDomain: respMsg.assistantDomain || 'general',
							guruPdfDownload: respMsg.guruPdfDownload && typeof respMsg.guruPdfDownload === 'object'
								? respMsg.guruPdfDownload : null,
							guruExcelDownload: respMsg.guruExcelDownload && typeof respMsg.guruExcelDownload === 'object'
								? respMsg.guruExcelDownload : null,
							guruExportEligible: respMsg.guruExportEligible !== false,
							guruExportPrepared: Boolean(respMsg.guruExportPrepared),
							guruExportCompleted: Boolean(respMsg.guruExportCompleted),
							offerHumanSupport: Boolean(respMsg.offerHumanSupport),
							supportNeedsEmail: Boolean(respMsg.supportNeedsEmail),
							type: 'text',
							createdAt: new Date(),
							self: false,
							avatar: 'AVATAR',
						};
					} catch (error) {
						console.error('Chat response failed; using configured fallback.', error);
						const fallbackContent = getConfiguredChatFallback(
							chatCtl.chatConfig || chatCfg || {},
							currentChat.chat_language
						);
						openAIResponse = {
							content: fallbackContent,
							quickQuestions: [],
							answerType: 'configured-fallback',
							helpUsed: false,
							sources: [],
							images: [],
							assistantPresentation: 'none',
							answerSource: 'general',
							assistantDomain: 'general',
							guruPdfDownload: null,
							guruExcelDownload: null,
							offerHumanSupport: false,
							supportNeedsEmail: false,
							type: 'text',
							createdAt: new Date(),
							self: false,
							avatar: 'AVATAR',
						};
					}
					if (pendingPageQuestionRef.current) {
						openAIResponse.quickQuestions = mergeCurrentPageQuestion(openAIResponse.quickQuestions);
						pendingPageQuestionRef.current = false;
					}
					if (!chatRef.current || chatRef.current.id !== currentChat.id) {
						// The answer belongs to a chat that is no longer displayed. It is
						// already persisted by the server and will appear when reopened.
						setHistoryRevision(value => value + 1);
						return;
					}

					// Update chat messages and add the OpenAI response
					setChat((prevChat) => ({
						...prevChat,
						chat_msgs: Array.isArray(prevChat.chat_msgs) ? [...prevChat.chat_msgs, openAIResponse] : [openAIResponse],
					}));
					// Add OpenAI response to the chat controller
					chatCtl.addMessage(openAIResponse);
					setHistoryRevision(value => value + 1);
				}
			} else {
				console.log('lastMessage', lastMessage);
				const currentChat = chatRef.current;

				//await chatCtl.addMessage(lastMessage);
				let usrId;
				var usr = sessionStorage.getItem('usr');
				if (usr) {
					usr = JSON.parse(usr);
					usrId = usr.usrId;
				}
				await postChatMessage(currentChat.id, lastMessage, usrId);
			}
		} else {
			setChat((prevChat) => ({
				...prevChat,
				chat_msgs: msgs,
			}));
		}

		console.log('<-- onMessagesChanged');
	};

	const handleSupportRequest = async ({message, additionalMessage, visitorEmail}) => {
		const currentChat = chatRef.current || {};
		const chatConfig = chatCtl.chatConfig || {};
		const chatConfigName = chatConfig.chat_cfg_name || currentChat.chat_cfg_name || props.instanceName || '';
		const messages = chatCtl.getMessages();
		let answerIndex = messages.indexOf(message);
		if (answerIndex < 0) answerIndex = messages.length - 1;
		let question = '';
		for (let index = answerIndex - 1; index >= 0; index -= 1) {
			if (messages[index].self) {
				question = messages[index].content || '';
				break;
			}
		}
		if (chatConfigName === 'synapcus-chat-guru' &&
			window.SynapcusGuru && typeof window.SynapcusGuru.requestSynapcus === 'function') {
			const context = window.SynapcusGuru.getContext();
			const payload = {
				senderName: context.userName || '',
				senderEmail: context.userEmail || '',
				organizationName: context.organizationName || '',
				departmentNames: context.departmentNames || [],
				currentPage: context.currentPage || '',
				language: context.language || currentChat.chat_language || '',
				question: question,
				answer: message.content || '',
				sources: Array.isArray(message.sources) ? message.sources : [],
				additionalMessage: additionalMessage || '',
				chatId: currentChat.id || '',
				chatConfigName: chatConfigName
			};
			const synapcusResponse = await window.SynapcusGuru.requestSynapcus(
				'/xrAppEval.xsp/rest/api?ssjsFct=sendGuruSupportMail',
				{
					method: 'POST',
					headers: {'Content-Type': 'application/json'},
					body: JSON.stringify({
						template: {data: [{name: 'payload', value: JSON.stringify(payload)}]}
					})
				}
			);
			const responseText = await synapcusResponse.text();
			console.log('Synapcus support response', synapcusResponse.status, responseText);
			if (!synapcusResponse.ok) {
				throw new Error('Synapcus konnte die Supportanfrage nicht senden (HTTP ' + synapcusResponse.status + ').');
			}

			let responseJson;
			try {
				responseJson = JSON.parse(responseText);
			} catch (error) {
				throw new Error('Synapcus lieferte keine gültige Versandbestätigung.');
			}
			if (!Array.isArray(responseJson) || String(responseJson[0]) !== '0') {
				throw new Error('Synapcus meldete einen Fehler beim Mailversand.');
			}
			const responseItems = responseJson[1] && responseJson[1].collection &&
				Array.isArray(responseJson[1].collection.items)
				? responseJson[1].collection.items : [];
			const responseData = responseItems[0] && Array.isArray(responseItems[0].data)
				? responseItems[0].data : [];
			const supportResult = responseData.reduce((result, item) => {
				if (item && item.nm) result[item.nm] = item.vl;
				return result;
			}, {});
			const mailConfirmed = supportResult.sent === true ||
				String(supportResult.sent).toLowerCase() === 'true' || String(supportResult.sent) === '1';
			if (!mailConfirmed) {
				throw new Error(supportResult.error || 'Synapcus hat den Mailversand nicht bestätigt.');
			}
			return {
				sent: true,
				copiedTo: supportResult.copiedTo || context.userEmail,
				recipient: supportResult.recipient || 'info@qalgo.de'
			};
		}

		return postSynapcusGuruSupportRequest(currentChat.id, {
			consent: true,
			additionalMessage: additionalMessage || '',
			visitorEmail: visitorEmail || ''
		});
	};

	const loadMyGuruPdfs = async () => {
		if (!window.SynapcusGuru || typeof window.SynapcusGuru.requestSynapcus !== 'function') {
			throw new Error('The authenticated Synapcus connection is unavailable.');
		}
		const response = await window.SynapcusGuru.requestSynapcus(
			'/xrAppEval.xsp/rest/api?ssjsFct=getMyGuruPdfSdocs',
			{method: 'GET', headers: {'Accept': 'application/json'}}
		);
		if (!response || !response.ok) {
			throw new Error('Synapcus returned HTTP ' + (response ? response.status : '0'));
		}
		let payload = await response.text();
		for (let pass = 0; pass < 2 && typeof payload === 'string'; pass += 1) {
			const text = payload.trim();
			if (!text) return [];
			payload = JSON.parse(text);
		}
		if (!payload || typeof payload !== 'object') return [];
		if (payload.err && Number(payload.err) !== 0) {
			throw new Error(String(payload.text || 'Synapcus could not load the PDF list.'));
		}
		const rows = Array.isArray(payload.ret) ? payload.ret : (Array.isArray(payload) ? payload : []);
		const toAbsoluteSynapcusUrl = value => {
			try {
				const url = new URL(String(value || '').trim(), window.location.href);
				if ((url.protocol !== 'https:' && url.protocol !== 'http:') || url.origin !== window.location.origin) return '';
				return url.toString();
			} catch (error) {
				return '';
			}
		};
		return rows.slice(0, 50).map(item => ({
			sdocUnid: String(item && item.sdocUnid || ''),
			fileId: String(item && item.fileId || ''),
			title: String(item && item.title || ''),
			fileName: String(item && item.fileName || ''),
			createdAt: String(item && item.createdAt || ''),
			fileSize: Number(item && item.fileSize || 0),
			sdocUrl: toAbsoluteSynapcusUrl(item && item.sdocUrl)
		})).filter(item => /^[A-F0-9]{32}$/i.test(item.sdocUnid) && /^https?:\/\//i.test(item.sdocUrl));
	};

	const downloadGuruPdf = async (pdf) => {
		const fileId = String(pdf && pdf.fileId || '').trim().toLowerCase();
		const filename = String(pdf && (pdf.filename || pdf.fileName) || '').trim();
		if (!/^[a-f0-9]{32}$/.test(fileId) || !/\.pdf$/i.test(filename) ||
			/[\x00-\x1f\x7f\\/]/.test(filename)) {
			throw new Error('Invalid private PDF metadata.');
		}
		if (!window.SynapcusGuru || typeof window.SynapcusGuru.requestSynforgeByApiKey !== 'function') {
			throw new Error('The authenticated Synforge connection is unavailable.');
		}
		const response = await window.SynapcusGuru.requestSynforgeByApiKey(
			'/openai/copilot/pdfs/' + encodeURIComponent(fileId),
			{method: 'GET', headers: {'Accept': 'application/pdf'}, cache: 'no-store'}
		);
		if (!response || !response.ok) {
			throw new Error('Synforge returned HTTP ' + (response ? response.status : '0'));
		}
		const blob = await response.blob();
		const objectUrl = window.URL.createObjectURL(blob);
		try {
			const link = document.createElement('a');
			link.href = objectUrl;
			link.download = filename.slice(0, 160);
			link.style.display = 'none';
			document.body.appendChild(link);
			link.click();
			link.remove();
		} finally {
			window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
		}
	};

	const loadMyGuruExcels = async () => {
		if (!window.SynapcusGuru || typeof window.SynapcusGuru.requestSynapcus !== 'function')
			throw new Error('The authenticated Synapcus connection is unavailable.');
		const response = await window.SynapcusGuru.requestSynapcus(
			'/xrAppEval.xsp/rest/api?ssjsFct=getMyGuruExcelSdocs',
			{method: 'GET', headers: {'Accept': 'application/json'}}
		);
		if (!response || !response.ok) throw new Error('Synapcus returned HTTP ' + (response ? response.status : '0'));
		let payload = await response.text();
		for (let pass = 0; pass < 2 && typeof payload === 'string'; pass += 1) {
			const text = payload.trim();
			if (!text) return [];
			payload = JSON.parse(text);
		}
		if (!payload || typeof payload !== 'object') return [];
		if (payload.err && Number(payload.err) !== 0) throw new Error(String(payload.text || 'Synapcus could not load the Excel list.'));
		const rows = Array.isArray(payload.ret) ? payload.ret : (Array.isArray(payload) ? payload : []);
		const toAbsoluteSynapcusUrl = value => {
			try {
				const url = new URL(String(value || '').trim(), window.location.href);
				if ((url.protocol !== 'https:' && url.protocol !== 'http:') || url.origin !== window.location.origin) return '';
				return url.toString();
			} catch (error) { return ''; }
		};
		return rows.slice(0, 50).map(item => ({sdocUnid: String(item && item.sdocUnid || ''),
			fileId: String(item && item.fileId || ''), title: String(item && item.title || ''),
			fileName: String(item && item.fileName || ''), createdAt: String(item && item.createdAt || ''),
			fileSize: Number(item && item.fileSize || 0), sdocUrl: toAbsoluteSynapcusUrl(item && item.sdocUrl)
		})).filter(item => /^[A-F0-9]{32}$/i.test(item.sdocUnid) && /^https?:\/\//i.test(item.sdocUrl));
	};

	const downloadGuruExcel = async (excel) => {
		const fileId = String(excel && excel.fileId || '').trim().toLowerCase();
		const filename = String(excel && (excel.filename || excel.fileName) || '').trim();
		if (!/^[a-f0-9]{32}$/.test(fileId) || !/\.xlsx$/i.test(filename) || /[\x00-\x1f\x7f\\/]/.test(filename))
			throw new Error('Invalid private Excel metadata.');
		if (!window.SynapcusGuru || typeof window.SynapcusGuru.requestSynforgeByApiKey !== 'function')
			throw new Error('The authenticated Synforge connection is unavailable.');
		const response = await window.SynapcusGuru.requestSynforgeByApiKey(
			'/openai/copilot/excels/' + encodeURIComponent(fileId),
			{method: 'GET', headers: {'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}, cache: 'no-store'}
		);
		if (!response || !response.ok) throw new Error('Synforge returned HTTP ' + (response ? response.status : '0'));
		const blob = await response.blob();
		const objectUrl = window.URL.createObjectURL(blob);
		try {
			const link = document.createElement('a');
			link.href = objectUrl; link.download = filename.slice(0, 160); link.style.display = 'none';
			document.body.appendChild(link); link.click(); link.remove();
		} finally { window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000); }
	};

	
	function _replacePlaceholders( txt, response) {
		const pattern = /\{(\w+)\}/g; // Matches placeholders like {anything}
		return txt.replace(pattern, (match, key) => {
			// Adjusting to check for and use the `content` property within each key's object
			if (response.hasOwnProperty(key) && response[key].hasOwnProperty('content')) {
				return response[key].content;
			}
			return match; // or `""` for default empty if you want to remove unmatched placeholders
		});
	}

		
	/**
	*	processSteps() - processes a steps section in the chat
	*/
	async function processSteps(msgs, steps) {
		isProcessingStepsRef.current = true;

		const currentChat = chatRef.current;

		for (let i = 0; i < steps.length; i++) {
			const step = steps[i];
			let isValid = false;
			let userInput = '';
			let attemptFailed = false; // Variable to track if a validation attempt has been made

			while (!isValid) {
				// Display the original question only on the first attempt
				if (!attemptFailed) {
					let msg = {
						content: _replacePlaceholders( step.question, chatCtl.getResponse()),
						type: 'text',
						answerType: 'predefined',
						createdAt: new Date(),
						self: false,
						avatar: 'AVATAR',
					};
					await chatCtl.addMessage(msg);
					await postChatMessage(currentChat.id, msg);
				}

				// Wait for user input. This uses a placeholder function to simulate waiting for input.
				userInput = await waitForUserInput();

				// Validate the user input according to the step's validation pattern.
				isValid = validateResponse(userInput.content, step.validationPattern); 
				if (!isValid) {
					// Inform the user that the input is invalid
					let errorMsg = {
						content: step.errorMessage,
						type: 'text',
						answerType: 'predefined',
						createdAt: new Date(),
						self: false,
						avatar: 'AVATAR',
					};
					await chatCtl.addMessage(errorMsg);
					await postChatMessage(currentChat.id, errorMsg);

					attemptFailed = true; // Mark the attempt as failed
				} else {
					// Post the field/value pair to the server
					if (step.fieldName) {
						chatCtl.addResponseField(step.fieldName, userInput); 
						await postChatFieldValue(currentChat.id, step.fieldName, userInput);
						await postChatMessage(currentChat.id, userInput);                 
					}
				}
			}
			console.log(`Step ${i + 1} completed with input: ${userInput.content}`);
		}
		isProcessingStepsRef.current = false;
		
		onMessagesChanged( chatCtl.getMessages());   // added because of delay in the promises, it forces the recall (?)
	}
	function validateResponse( response, validationPattern) {
		
		//if( response.length >= 4 || validationPattern==='none')
		if( validationPattern==='none')			
			return true;
		
		const regex = new RegExp( validationPattern);
		return regex.test( response);
	}
	async function waitForUserInput() {
		return new Promise((resolve) => {
			const initialMessageCount = chatCtl.getMessages().length;

			const checkForNewInput = () => {
				const messages = chatCtl.getMessages();
				if (messages.length > initialMessageCount) {
					const newMessage = messages[messages.length - 1];

					// Additional checks can be added here to ensure the message
					// is indeed a user input relevant to the current step
					if (newMessage.self === true) { 
						resolve( newMessage);
					}
				} else {
					// Re-check after a delay if no new message is detected
					setTimeout( checkForNewInput, 500); // Adjust delay as needed
				}
			};

			// Start checking for new input
			checkForNewInput();		
		});
	}

						
	const handleSubmitDlgChat = () => {
		console.log( 'handleSubmitDlgChat SUBMITTING >>>>', changesDlgChat);
	}

	const activeChatConfigName = (chatCtl && chatCtl.chatConfig && chatCtl.chatConfig.chat_cfg_name) ||
		(chatRef.current && chatRef.current.chat_cfg_name) || props.instanceName || '';
	let historyUser = {};
	try { historyUser = JSON.parse(sessionStorage.getItem('usr') || '{}'); } catch (error) {}
	const historyUiEnabled = activeChatConfigName === 'synapcus-chat-guru' && Boolean(historyUser.usrId);
	const activeLanguage = resolveGuruUiLanguage({
		chatConfigName: activeChatConfigName,
		browserLanguages: window.navigator.languages,
		browserLanguage: window.navigator.language,
		chatLanguage: chatRef.current && chatRef.current.chat_language
	});
	const activeUiLabels = (chatCtl && chatCtl.chatConfig &&
		(chatCtl.chatConfig.chatUiLabels || chatCtl.chatConfig.chat_ui_labels)) || {};
	const renderChatSurface = ({
		openHistory,
		showHistoryAction,
		activeChat,
		chatActionsDisabled,
		onRenameChat,
		onTogglePinChat,
		onDeleteChat
	} = {}) => (
		<QeeMuiChat
			height={height || '100%'}
			chatController={chatCtl}
			onSupportRequest={handleSupportRequest}
			onResetChat={handleResetChat}
			onNewChat={handleResetChat}
			onLoadGuruPdfs={loadMyGuruPdfs}
			onDownloadGuruPdf={downloadGuruPdf}
			onLoadGuruExcels={loadMyGuruExcels}
			onDownloadGuruExcel={downloadGuruExcel}
			enableChatReset={!historyUiEnabled && !isP2P && Boolean(chatCfg)}
			onOpenChatHistory={openHistory}
			showChatHistoryAction={Boolean(showHistoryAction)}
			activeGuruChat={activeChat}
			guruChatActionsDisabled={Boolean(chatActionsDisabled)}
			onRenameGuruChat={onRenameChat}
			onTogglePinGuruChat={onTogglePinChat}
			onDeleteGuruChat={onDeleteChat}
			supportLanguage={activeLanguage}
			enableHumanSupport={['synapcus-chat-guru', 'synapcus-chat-qalgo1'].includes(activeChatConfigName)}
			enableGuruPdfs={activeChatConfigName === 'synapcus-chat-guru'}
			enableGuruExcels={activeChatConfigName === 'synapcus-chat-guru'}
			supportNeedsEmail={activeChatConfigName === 'synapcus-chat-qalgo1'}
			enableLeylaBoss={(chatCtl.chatConfig && chatCtl.chatConfig.enableLeylaBoss) !== false}
			leylaBossCooldown={(chatCtl.chatConfig && chatCtl.chatConfig.leylaBossCooldown) || 3}
			assistantAvatarLabels={(chatCtl.chatConfig && chatCtl.chatConfig.assistantAvatarLabels) || {}}
			chatUiLabels={activeUiLabels}
			supportUserEmail={window.SynapcusGuru && typeof window.SynapcusGuru.getContext === 'function'
				? window.SynapcusGuru.getContext().userEmail
				: ''}
		/>
	);
					
	return (		
		<div
			style={{
				marginTop: 0,
				height: '100dvh',
				maxHeight: '100dvh',
				minHeight: 0,
				overflow: 'hidden',
				display: 'flex',
				flexDirection: 'column'
			}}
		>
            {isReady && chatCtl ? (
				<>
				{isError ?			
					<>CONFIGURATION ERROR: {isError}</>
				:
					historyUiEnabled ? (
						<GuruChatWorkspace
							currentChatId={(chatRef.current && chatRef.current.id) || ''}
							language={activeLanguage}
							configuredLabels={activeUiLabels}
							historyRevision={historyRevision}
							initialHistoryOpen={standaloneLaunchContext.showHistory}
							onNewChat={handleResetChat}
							onSelectChat={handleSelectHistoryChat}
							onLoadGuruPdfs={loadMyGuruPdfs}
							onLoadGuruExcels={loadMyGuruExcels}
							onDownloadGuruPdf={downloadGuruPdf}
							onDownloadGuruExcel={downloadGuruExcel}
							renderChat={renderChatSurface}
						/>
					) : renderChatSurface()
				}
				</>
            ) : (
				<WaitProgressLinear />
            )}
        </div>		
	)
})	
export default withStyles(styles)(QeeChatEmbedded);
